import { SiteItemData, SiteVideoData } from "../../../../interfaces";
import { MediaContentTypes } from "../../../../../constants/contentTypes";
import { getNewlinkTargetProperties } from "../../../../widgetsUtils";

export type LightboxItem = SiteItemData & { hidePrerollAds?: boolean, prerollIndex?: number };

export interface LightboxFeedAdSettings {
    showPrerollAds: boolean
    firstAdPlace: number
    maxAds: number
    repeatAds: number
}

export interface LightboxFeed {
    items: LightboxItem[]
    adSettings: LightboxFeedAdSettings
}

export const LIGHTBOX_VIDEO_BEHAVIOR = {
    isAutoPlaying: true,
    isLoopPlaying: false,
    isPlayerBarVisible: true,
    isSoundButtonVisible: true,
    isMuteAvailable: true,
};

export const getLightboxFeed = (pageJson: any, componentName: string, sharedVideoPageId: string, sharedMediaId: string): LightboxFeed => {
    const components = (pageJson && pageJson.components) || [];
    const feedComponent = components.find((component) => component && component.name === componentName);
    if (!feedComponent) return { items: [], adSettings: null };

    const items = (feedComponent.items || [])
        .map(mapFeedItemToLightboxItem)
        .filter((item: LightboxItem) => item && !isSameVideo(item.media as SiteVideoData, sharedVideoPageId, sharedMediaId));
    const setting = feedComponent.setting || {};
    const showPrerollAds = feedComponent.showPrerollAds !== undefined ? feedComponent.showPrerollAds : setting.showPrerollAds;
    const { firstAdPlace = 0, maxAds = 0, repeatAds = 0 } = setting;

    return { items, adSettings: { showPrerollAds: !!showPrerollAds, firstAdPlace, maxAds, repeatAds } };
}

export const applyFeedPrerolls = (items: LightboxItem[], adSettings: LightboxFeedAdSettings): LightboxItem[] => {
    if (!adSettings || !adSettings.showPrerollAds) return items.map((item) => ({ ...item, hidePrerollAds: true, prerollIndex: undefined }));

    const { firstAdPlace, maxAds, repeatAds } = adSettings;
    const length = items.length;
    const totalAds = firstAdPlace > length ? 0 : Math.min(maxAds, length - firstAdPlace, Math.floor((length - firstAdPlace) / repeatAds + 1));
    let adsLeft = totalAds;

    return items.map((item, i) => {
        const isAdSlot = adsLeft > 0 && i >= firstAdPlace && (i - firstAdPlace) % (repeatAds + 1) === 0;
        if (!isAdSlot) return { ...item, hidePrerollAds: true, prerollIndex: undefined };

        const prerollIndex = totalAds - adsLeft + 1;
        adsLeft--;
        return { ...item, hidePrerollAds: false, prerollIndex };
    });
}

const isSameVideo = (media: SiteVideoData, sharedVideoPageId: string, sharedMediaId: string) =>
    (!!sharedVideoPageId && media.videoPageId === sharedVideoPageId) || (!!sharedMediaId && media.mediaId === sharedMediaId);

// newYnetTvVertical items keep the video in `videoItem`; ynetRylz items keep it in `mediaData[0].item` (and may be images)
const getFeedItemVideo = (item: any) => {
    if (!item) return null;
    if (item.videoItem) return item.videoItem;
    const mediaSource = item.mediaData && item.mediaData[0];
    return mediaSource && mediaSource.type === "video" ? mediaSource.item : null;
}

const mapFeedItemToLightboxItem = (item: any, index: number): LightboxItem => {
    const video = getFeedItemVideo(item);
    if (!video || !video.url) return null;

    const link = item.linkData && item.linkData.link;
    const mediaId = video.videoId != null ? String(video.videoId) : (video.mediaId != null ? String(video.mediaId) : "");

    return {
        itemId: `videoPageFeed_${mediaId || "item"}_${index}`,
        title: item.title || "",
        subTitle: item.subTitle || "",
        titleLink: link && link.href ? link.href : undefined,
        titleTargetLink: getNewlinkTargetProperties(),
        media: {
            ...LIGHTBOX_VIDEO_BEHAVIOR,
            mediaType: MediaContentTypes.MEDIA_VIDEO,
            mediaId,
            url: video.url,
            downGradeUrl: "",
            poster: (video.opening_img && video.opening_img.src) || video.src || "",
            promoPath: video.promoUrl || "",
            duration: video.duration,
            videoPageId: video.videoPageId ? String(video.videoPageId) : "",
            credit: item.creditVideo || video.credit || "",
            videoType: "vertical_video",
            blockAbroadViews: false,
        } as SiteVideoData,
    };
}
